import React, { useEffect, useRef, useState } from "react";
import {
  DocumentContext,
  lastSaveSucceeded
} from "../../components/loan_tools/loan_tool";
import { flushPendingSaves } from "../../services/pending_saves";

// Build stamp — a REAL (minification-surviving) side effect, so each build emits
// a new bundle hash. Comment-only changes get stripped by the production build
// and leave the hash unchanged, which is why the Update banner correctly stays
// silent for them. Bump this value to force a genuinely new build. Harmless: it
// only records the build id on window.
if (typeof window !== "undefined") {
  window.__LANIS_BUILD__ = "2026-10-05-1";
}

/**
 * UpdateBanner — tells the LO when a newer version of the app has been deployed,
 * so a long-open single-page session never keeps running stale code without the
 * user knowing. Purely about the app's CODE (not loan data): the pipeline already
 * refreshes its own data.
 *
 * How it detects a new deploy (no service worker in this app):
 *   - The moment the tab mounts, we record the EXACT hashed JS bundles THIS tab
 *     actually loaded, read from the page's own <script> tags. That is a fixed,
 *     deterministic fingerprint of the version this tab is running.
 *   - On focus and a slow timer we fetch a fresh index.html and read the bundle
 *     names IT references (the currently-deployed version).
 *   - If the deployed bundles differ from the ones this tab is running, a new
 *     build is live -> show the banner.
 *
 * This is deliberately NOT a "baseline = first fetched index.html" snapshot: that
 * approach captured the baseline at an arbitrary later moment, so a tab could
 * snapshot a version newer than the code it was actually running and then never
 * warn (a false "you're current"), while sibling tabs warned — the inconsistency
 * Randy saw. Comparing against what the tab truly loaded is race-free: banner
 * shown <=> this tab is genuinely stale; no banner <=> genuinely current.
 *
 * "Update now" reloads to pick up the new bundle; the server only ever serves the
 * one latest deployment (content-hashed filenames), so a reload always lands on
 * the most current build in full — never a previous one.
 */

// Version fingerprint = the hashed MAIN entry bundle (e.g.
// "/static/js/main.9f2c.js"), whose hash changes on essentially every build.
// We intentionally match ONLY main.* and ignore lazy "*.chunk.js" files: those
// are code-split on demand (e.g. the PDF helper) and appear in the page's live
// <script> tags but NOT in index.html, which would otherwise look like a version
// mismatch and raise a false "update available". Origin/query are ignored so the
// running document and the fetched index.html compare on the same basis.
function bundleSignatureFromHtml(html) {
  const matches = html.match(/\/static\/js\/main\.[A-Za-z0-9]+\.js/g) || [];
  return Array.from(new Set(matches)).sort().join("|");
}

// The bundles THIS tab actually loaded, from its own <script> tags.
function runningBundleSignature() {
  try {
    const srcs = Array.from(window.document.getElementsByTagName("script"))
      .map(s => s.getAttribute("src") || "")
      .join(" ");
    return bundleSignatureFromHtml(srcs);
  } catch (e) {
    return "";
  }
}

export default function UpdateBanner() {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [saving, setSaving] = useState(false);
  // Present on loan pages (the open loan's state + actions), undefined elsewhere.
  const documentCtx = React.useContext(DocumentContext);
  // The version this tab is running — captured once, synchronously, at mount.
  const runningRef = useRef(runningBundleSignature());

  useEffect(() => {
    let active = true;

    async function deployedSignature() {
      try {
        const res = await fetch("/index.html?_v=" + Date.now(), {
          cache: "no-store"
        });
        if (!res.ok) return null;
        return bundleSignatureFromHtml(await res.text());
      } catch (e) {
        return null; // offline / transient — try again next tick
      }
    }

    async function check() {
      if (window.document.visibilityState === "hidden") return;
      const running = runningRef.current;
      // If we couldn't read this tab's own bundles, stay silent rather than risk
      // a false alarm.
      if (!running) return;
      const deployed = await deployedSignature();
      if (!active || deployed == null || deployed === "") return;
      if (deployed !== running) {
        setUpdateAvailable(true);
      }
    }

    check();
    const onFocus = () => check();
    // Switching BETWEEN tabs in the SAME browser window fires visibilitychange
    // but often NOT window "focus" (the window never lost OS focus). Without
    // this, a loan tab opened before a deploy would not notice the new build
    // when the LO tabbed back to it — the banner only reappeared on the slow
    // 5-min timer (or a hard reload). Re-check the moment the tab becomes
    // visible so the Update banner shows on a loan page as reliably as on the
    // pipeline.
    const onVisible = () => {
      if (window.document.visibilityState === "visible") check();
    };
    window.addEventListener("focus", onFocus);
    window.document.addEventListener("visibilitychange", onVisible);
    const interval = setInterval(check, 5 * 60 * 1000);
    return () => {
      active = false;
      window.removeEventListener("focus", onFocus);
      window.document.removeEventListener("visibilitychange", onVisible);
      clearInterval(interval);
    };
  }, []);

  if (!updateAvailable) return null;

  // Updating reloads THIS tab to pick up the new bundle. A reload discards any
  // unsaved loan edits, so the LO is no longer trusted to remember to save:
  // with unsaved edits open, Update SAVES the loan first and reloads only if that
  // save succeeded. If the save fails (e.g. an expired login session), the tab
  // does NOT reload; the red NOT SAVED bar explains how to save the edits.
  // Other tabs are untouched and keep their own banner.
  async function update() {
    const docState = documentCtx && documentCtx[2];
    const docActions = documentCtx && documentCtx[3];

    if (docState && docState.dirty && docActions) {
      setSaving(true);
      await docActions.saveDocument();
      setSaving(false);
      if (!lastSaveSucceeded()) {
        return;
      }
      // Let the saved state render first, so the browser's "Leave site?"
      // warning (armed while there were unsaved edits) stands down.
      await new Promise(resolve => setTimeout(resolve, 150));
      window.location.reload();
      return;
    }

    // Finish any background saves first (the Pipeline auto-saves its edits a
    // moment after they're made). If one fails, stay put: the page shows its
    // own red NOT SAVED bar with what to do.
    setSaving(true);
    const pendingSaved = await flushPendingSaves();
    setSaving(false);
    if (!pendingSaved) {
      return;
    }

    // Nothing unsaved now, so say so: the LO shouldn't have to wonder whether
    // clicking OK loses work.
    const reassurance = docState
      ? "Your loan is saved."
      : window.location.pathname === "/"
      ? "Your Pipeline changes (Status, dates, details) are saved."
      : "";
    const ok = window.confirm(
      (reassurance ? reassurance + "\n\n" : "") +
        "Update to the new version now?\n\n" +
        "This reloads this tab only; your other tabs are not affected."
    );
    if (ok) {
      window.location.reload();
    }
  }

  return (
    <div className="update-banner">
      <span className="update-banner-text">
        A new version of LANIS is available. SAVE YOUR OPEN WORK, then update.
      </span>
      <button
        className="button update-banner-button"
        onClick={update}
        disabled={saving}
      >
        {saving ? "Saving..." : "Update now"}
      </button>
    </div>
  );
}
